/* Minori · gli exchange registrati, con creazione e modifica.
 *
 * Perché una sezione a sé sotto gli account: un account non si crea senza un
 * exchange, e finora l'elenco degli exchange esisteva solo dentro il menu a
 * tendina del modulo account — cioè si potevano usare, non si potevano né
 * aggiungere né correggere. Uno slug sbagliato bloccava l'account e non c'era
 * nessun posto da cui rimediare.
 *
 * Cosa il backend NON dà, e che quindi qui non compare inventato:
 *  - l'elenco degli slug validi di ccxt: nessun endpoint lo espone. Lo slug si
 *    scrive a mano e la verifica arriva dopo il salvataggio, da
 *    `isCCXTSupported` che il server calcola a ogni caricamento. La richiesta è
 *    in RICHIESTE-API.md;
 *  - quali funzioni supporta un exchange (spot, margine, prelievi): niente.
 *
 * `slug` è UNIQUE a database: il duplicato si ferma qui, prima dell'invio,
 * perché un 500 di vincolo violato si legge come "non ha funzionato" e non come
 * "quel nome è già di un altro".
 */

import { useEffect, useState } from 'react';
import { Avvertenza, Distintivo, Pulsante, Selettore } from '../../componenti';
import { Campo } from '../../componenti/Modulo';
import { Modale } from '../../componenti/Sovrapposizioni';
import { ContenitoreDati, ScheletroLista } from '../../componenti/Stati';
import { exchanges } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import type { StatoDati } from '../../api/usaRichiesta';
import type { Exchange } from '../../api/tipi';

export function SezioneExchange({
  lista,
  stato,
  messaggioErrore,
  onRicarica,
  contaAccount,
  onAvviso,
}: {
  lista: Exchange[];
  stato: StatoDati;
  messaggioErrore?: string;
  onRicarica: () => void;
  /** Quanti account usano ogni exchange: cambiare lo slug sotto a un account
   *  che opera è la cosa che qui si può rompere, quindi il numero si mostra. */
  contaAccount: Map<number, number>;
  onAvviso: (testo: string, esito: 'riuscito' | 'fallito') => void;
}) {
  const [apreAggiunta, setApreAggiunta] = useState(false);
  const [daModificare, setDaModificare] = useState<Exchange | null>(null);

  return (
    <div className="sb-colonna" style={{ marginTop: 'var(--sb-s-10)' }}>
      <div className="sb-riga">
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 'var(--sb-peso-semi)', fontSize: 'var(--sb-t-14)' }}>
            Exchange registrati
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
            {lista.length === 0
              ? 'nessun exchange'
              : `${lista.length} · lo slug deve essere quello con cui ccxt conosce l'exchange`}
          </div>
        </div>
        <Pulsante variante="tenue" onClick={() => setApreAggiunta(true)}>
          Aggiungi
        </Pulsante>
      </div>

      <ContenitoreDati
        stato={stato}
        causaVuoto="Nessun exchange registrato: senza, non si può creare nessun account."
        azioneVuoto={{ etichetta: 'Aggiungi il primo', onClick: () => setApreAggiunta(true) }}
        messaggioErrore={messaggioErrore}
        onRiprova={onRicarica}
        scheletro={<ScheletroLista righe={2} />}
      >
        <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
          {lista.map((e) => {
            const usato = contaAccount.get(e.id) ?? 0;
            const rotto = e.isCCXT && e.isCCXTSupported === false;
            return (
              <article
                key={e.id}
                className={`sb-m-account${rotto ? ' sb-m-account--attenzione' : ''}`}
              >
                <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontWeight: 'var(--sb-peso-forte)', fontSize: 'var(--sb-t-135)' }}>
                      {e.name}
                    </div>
                    <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
                      {e.slug} ·{' '}
                      {usato === 0
                        ? 'nessun account collegato'
                        : `${usato} ${usato === 1 ? 'account collegato' : 'account collegati'}`}
                    </div>
                  </div>
                  <Distintivo tono={rotto ? 'cautela' : 'neutro'}>
                    {!e.isCCXT ? 'nativo' : rotto ? 'non più in ccxt' : 'via ccxt'}
                  </Distintivo>
                </div>

                {rotto && (
                  <div style={{ marginTop: 'var(--sb-s-5)' }}>
                    <Avvertenza livello="cautela">
                      La versione di ccxt installata non riconosce lo slug "{e.slug}": gli account
                      su questo exchange non possono operare finché lo slug non viene corretto.
                    </Avvertenza>
                  </div>
                )}

                <div className="sb-riga" style={{ marginTop: 'var(--sb-s-6)' }}>
                  <span style={{ flex: 1 }} />
                  <Pulsante variante="tenue" onClick={() => setDaModificare(e)}>
                    Modifica…
                  </Pulsante>
                </div>
              </article>
            );
          })}
        </div>
      </ContenitoreDati>

      <ModuloExchange
        aperto={apreAggiunta}
        exchange={null}
        esistenti={lista}
        accountCollegati={0}
        onChiudi={() => setApreAggiunta(false)}
        onSalvato={(nome) => {
          setApreAggiunta(false);
          onAvviso(`Exchange "${nome}" creato.`, 'riuscito');
          onRicarica();
        }}
      />

      <ModuloExchange
        aperto={daModificare != null}
        exchange={daModificare}
        esistenti={lista}
        accountCollegati={daModificare ? (contaAccount.get(daModificare.id) ?? 0) : 0}
        onChiudi={() => setDaModificare(null)}
        onSalvato={(nome) => {
          setDaModificare(null);
          onAvviso(`Exchange "${nome}" aggiornato.`, 'riuscito');
          onRicarica();
        }}
      />
    </div>
  );
}

/* ==========================================================================
 * Il modulo, uno solo per creazione e modifica
 * ======================================================================== */

function ModuloExchange({
  aperto,
  exchange,
  esistenti,
  accountCollegati,
  onChiudi,
  onSalvato,
}: {
  aperto: boolean;
  /** `null` = creazione. */
  exchange: Exchange | null;
  esistenti: Exchange[];
  accountCollegati: number;
  onChiudi: () => void;
  onSalvato: (nome: string) => void;
}) {
  const inModifica = exchange != null;
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');
  const [tramiteCcxt, setTramiteCcxt] = useState('si');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /* All'apertura si carica il valore corrente: un modulo di modifica che parte
     vuoto fa credere che i valori vecchi siano stati persi. */
  useEffect(() => {
    if (!aperto) return;
    setNome(exchange?.name ?? '');
    setSlug(exchange?.slug ?? '');
    setTramiteCcxt(exchange == null || exchange.isCCXT ? 'si' : 'no');
    setErrore(null);
  }, [aperto, exchange]);

  const nomePulito = nome.trim();
  /* Gli slug di ccxt sono minuscoli senza spazi: normalizzare qui evita di
     salvare "Binance " e passare un'ora a chiedersi perché non è supportato. */
  const slugPulito = slug.trim().toLowerCase();

  const slugDuplicato =
    slugPulito !== '' &&
    esistenti.some((e) => e.slug.toLowerCase() === slugPulito && e.id !== exchange?.id);
  const slugMalformato = slugPulito !== '' && !/^[a-z0-9_.-]+$/.test(slugPulito);
  const slugCambiato = inModifica && slugPulito !== exchange!.slug.toLowerCase();

  const valido =
    nomePulito !== '' && slugPulito !== '' && !slugDuplicato && !slugMalformato && !inCorso;

  async function invia() {
    if (!valido) return;
    setInCorso(true);
    setErrore(null);
    const dati = { name: nomePulito, slug: slugPulito, isCCXT: tramiteCcxt === 'si' };
    try {
      if (inModifica) await exchanges.aggiorna(exchange!.id, dati);
      else await exchanges.crea(dati);
      onSalvato(nomePulito);
    } catch (e) {
      setErrore(
        e instanceof ErroreApi
          ? e.stato === 409 || e.stato === 500
            ? 'Salvataggio non riuscito: lo slug potrebbe essere già in uso.'
            : e.message
          : 'Salvataggio non riuscito.',
      );
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={aperto}
      onChiudi={() => {
        setErrore(null);
        onChiudi();
      }}
      titolo={inModifica ? "Modifica l'exchange" : 'Aggiungi un exchange'}
      sottotitolo={inModifica ? exchange!.name : "lo slug è quello che usa ccxt, non il nome commerciale"}
      pieda={
        <Pulsante variante="primario" largo onClick={invia} disabled={!valido}>
          {inCorso ? 'Salvataggio in corso…' : inModifica ? 'Salva le modifiche' : "Crea l'exchange"}
        </Pulsante>
      }
    >
      <div className="sb-colonna">
        <Campo
          etichetta="Nome"
          testuale
          suggerimento="come compare nelle liste"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          autoComplete="off"
        />

        <Campo
          etichetta="Slug"
          testuale
          suggerimento="minuscolo, senza spazi — es. binance, kucoin, coinex"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          errore={
            slugDuplicato
              ? 'Un altro exchange usa già questo slug.'
              : slugMalformato
                ? 'Ammessi solo lettere minuscole, cifre, trattino, punto e trattino basso.'
                : null
          }
        />

        <Selettore
          etichetta="Come ci si collega"
          valore={tramiteCcxt}
          voci={[
            { valore: 'si', etichetta: 'Tramite ccxt (quasi sempre)' },
            { valore: 'no', etichetta: 'Integrazione nativa di SemBot' },
          ]}
          onCambia={setTramiteCcxt}
        />

        {tramiteCcxt === 'si' && (
          <Avvertenza livello="nota">
            Lo slug non si può verificare da qui: nessun endpoint espone l'elenco di ccxt. Se non è
            valido, l'exchange si crea comunque e comparirà con l'etichetta «non più in ccxt» appena
            l'elenco si ricarica — è lì che si vede se hai scritto giusto.
          </Avvertenza>
        )}

        {slugCambiato && accountCollegati > 0 && (
          <Avvertenza livello="grave">
            {accountCollegati === 1
              ? "C'è 1 account collegato a questo exchange"
              : `Ci sono ${accountCollegati} account collegati a questo exchange`}
            : le loro chiavi API valgono per «{exchange!.slug}», non per «{slugPulito}». Cambiando
            slug quelle chiavi vengono presentate all'exchange sbagliato e ogni operazione dei bot
            che le usano fallirà.
          </Avvertenza>
        )}

        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}
      </div>
    </Modale>
  );
}
